import { Button } from '@/components/ui/Button';
import { PUBLIC_BODY_CLASS } from '../public-text';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { ArrowRightIcon } from '@/assets/icons/ArrowRightIcon';
import { useManualSlider } from '../../hooks/useManualSlider';
import { DEFAULT_HERO_IMAGE } from '../default-images';
import { CtaLink } from '../CtaLink';
import type { SectionRendererProps } from '../section-renderer.types';

interface HeroContent {
  eyebrow: string;
  title: string;
  subtitle?: string;
  body?: string[];
  images?: string[];
  ctaLabel?: string;
  ctaUrl?: string;
}

/**
 * Left accent-bar color, confirmed per page from Figma - Home keeps the
 * green bar, Benefits uses navy (`#001928`), Investors uses maroon
 * (`#4F0A33`, MYN-214's changes doc) - so this can't be a single shared
 * class on the renderer.
 */
const ACCENT_BAR_CLASS: Record<string, string> = {
  home: 'border-dkgreen',
  benefits: 'border-[#001928]',
  investors: 'border-maroon',
  // trusted_p_ch.docx: "The vertical line in the hero/header should be
  // #2B958E" - `--color-teal2`, a different token from Investors' teal/maroon.
  // Was `border-teal2` (#2B958E) per trusted_p_ch.docx; MYN-327's own content
  // document names #001928 for the bar, headline and button (FR-318, design
  // §57 D4), superseding A-127 for these three elements.
  'trusted-partners': 'border-[#001928]',
};

// Eyebrow/title color, same per-page reasoning as the accent bar above -
// Home/Benefits both use the dkgreen eyebrow + navy (text-text-primary)
// title; Investors' changes doc explicitly calls for teal (`--color-teal`,
// #00405C) eyebrow and maroon headline instead. Trusted Partners'
// trusted_p_ch.docx calls for the OPPOSITE split: eyebrow in teal (#00405C,
// A-117) and headline in teal2 (#2B958E) - two distinct tokens, neither
// reused from Investors' pairing.
const EYEBROW_CLASS: Record<string, string> = {
  investors: 'text-teal',
  'trusted-partners': 'text-teal',
};
const TITLE_CLASS: Record<string, string> = {
  investors: 'text-maroon',
  'trusted-partners': 'text-navy',
};
// Hero CTA button variant per page - Home/Benefits use the shared `primary`
// (dkgreen); Investors' changes doc calls for the same maroon as its accent
// bar/headline. Trusted Partners matched its own accent too, teal2, until
// MYN-327's content document named #001928 outright (FR-318, design §57 D4)
// - the literal hex outranks MYN-328's generic "any MNR blue", which is why
// this is `navy` rather than the lighter `teal` variant that ticket added.
const CTA_BUTTON_VARIANT: Record<string, 'primary' | 'maroon' | 'teal2' | 'navy'> = {
  investors: 'maroon',
  'trusted-partners': 'navy',
};

/** Hero section (FR-108, FR-119) - Home and Benefits, same renderer, own content per page. */
export function HeroRenderer({ section, pageSlug }: SectionRendererProps) {
  const c = section.content as unknown as HeroContent;
  // Falls back to this page's default hero image when the CMS has none set,
  // so a fresh database still shows a real photo instead of a blank slot -
  // see default-images.ts. An admin-uploaded image always takes priority.
  const defaultImage = DEFAULT_HERO_IMAGE[pageSlug];
  const images = c.images?.length ? c.images : defaultImage ? [defaultImage] : [];
  const { index, next, prev } = useManualSlider(images.length);
  const accentBarClass = ACCENT_BAR_CLASS[pageSlug] ?? ACCENT_BAR_CLASS.home;
  const eyebrowClass = EYEBROW_CLASS[pageSlug] ?? 'text-dkgreen';
  const titleClass = TITLE_CLASS[pageSlug] ?? 'text-text-primary';
  const ctaVariant = CTA_BUTTON_VARIANT[pageSlug] ?? 'primary';

  return (
    <section className="w-full bg-white">
      <div className="mx-auto flex w-full max-w-328 flex-col items-center gap-10 px-4 py-16 lg:flex-row">
        {/* Vertical accent bar down the left of the text column, colored
          per page (Home: green, Benefits: navy) per Figma. */}
        <div className={`flex flex-1 flex-col gap-4 border-l-4 pl-6 ${accentBarClass}`}>
          <p className={`text-p6 font-semibold uppercase ${eyebrowClass}`}>{c.eyebrow}</p>
          <h1 className={`text-h1 ${titleClass}`}>{c.title}</h1>
          {c.subtitle && <p className="text-p4 text-text-secondary">{c.subtitle}</p>}
          {c.body?.map((paragraph, i) => (
            <p key={i} className={PUBLIC_BODY_CLASS}>
              {paragraph}
            </p>
          ))}
          {c.ctaLabel && c.ctaUrl && (
            <Button asChild variant={ctaVariant} className="w-fit">
              <CtaLink to={c.ctaUrl}>{c.ctaLabel}</CtaLink>
            </Button>
          )}
        </div>
        {images.length > 0 && (
          <div className="relative w-full flex-1">
            <img
              src={images[index]}
              alt=""
              className="aspect-4/3 w-full rounded-2xl object-cover"
            />
            {images.length > 1 && (
              <div className="absolute bottom-4 right-4 flex gap-2">
                <button
                  type="button"
                  aria-label="Previous image"
                  onClick={prev}
                  className="bg-white/90 hover:bg-white flex size-9 cursor-pointer items-center justify-center rounded-full text-navy"
                >
                  <ArrowLeftIcon size={16} />
                </button>
                <button
                  type="button"
                  aria-label="Next image"
                  onClick={next}
                  className="bg-white/90 hover:bg-white flex size-9 cursor-pointer items-center justify-center rounded-full text-navy"
                >
                  <ArrowRightIcon size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
